export interface MangaPanel {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FittedMangaPanel {
  imageWidth: number;
  imageHeight: number;
  left: number;
  top: number;
  visibleWidth: number;
  visibleHeight: number;
}

export function fitMangaPanelToViewport(
  page: Pick<MangaPanel, "width" | "height">,
  panel: MangaPanel,
  viewport: Pick<MangaPanel, "width" | "height">
): FittedMangaPanel {
  const landscape = viewport.width > viewport.height;
  const horizontalInset = landscape ? 8 : 12;
  const topInset = landscape ? 8 : 68;
  const bottomInset = landscape ? 8 : 92;
  const availableWidth = Math.max(1, viewport.width - horizontalInset * 2);
  const availableHeight = Math.max(1, viewport.height - topInset - bottomInset);
  const panelWidth = Math.max(1, page.width * panel.width);
  const panelHeight = Math.max(1, page.height * panel.height);
  const scale = Math.min(availableWidth / panelWidth, availableHeight / panelHeight);
  const imageWidth = page.width * scale;
  const imageHeight = page.height * scale;

  return {
    imageWidth,
    imageHeight,
    left: viewport.width / 2 - (panel.x + panel.width / 2) * imageWidth,
    top: topInset + availableHeight / 2 - (panel.y + panel.height / 2) * imageHeight,
    visibleWidth: panelWidth * scale,
    visibleHeight: panelHeight * scale
  };
}

export function oppositeSupportSide(
  panel: MangaPanel | undefined,
  region: Pick<MangaPanel, "x" | "width">
): "left" | "right" {
  const visibleCentre = panel ? panel.x + panel.width / 2 : 0.5;
  return region.x + region.width / 2 <= visibleCentre ? "right" : "left";
}

export function regionIsInsidePanel(
  panel: MangaPanel | undefined,
  region: Pick<MangaPanel, "x" | "y" | "width" | "height">
): boolean {
  if (!panel) return true;
  const centreX = region.x + region.width / 2;
  const centreY = region.y + region.height / 2;
  return centreX >= panel.x
    && centreX <= panel.x + panel.width
    && centreY >= panel.y
    && centreY <= panel.y + panel.height;
}

type PixelRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

type Separator = {
  axis: "horizontal" | "vertical";
  start: number;
  end: number;
  score: number;
};

const panelCache = new Map<string, Promise<MangaPanel[]>>();
const maxDetectedPanels = 16;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function inkRatio(
  ink: Uint8Array,
  imageWidth: number,
  rect: PixelRect,
  axis: Separator["axis"],
  coordinate: number
): number {
  let inkPixels = 0;
  let pixelCount = 0;
  if (axis === "horizontal") {
    for (let x = rect.left; x < rect.right; x += 1) {
      inkPixels += ink[coordinate * imageWidth + x];
      pixelCount += 1;
    }
  } else {
    for (let y = rect.top; y < rect.bottom; y += 1) {
      inkPixels += ink[y * imageWidth + coordinate];
      pixelCount += 1;
    }
  }
  return pixelCount > 0 ? inkPixels / pixelCount : 1;
}

function separatorFor(
  ink: Uint8Array,
  imageWidth: number,
  rect: PixelRect,
  axis: Separator["axis"],
  pageWidth: number,
  pageHeight: number
): Separator | null {
  const start = axis === "horizontal" ? rect.top : rect.left;
  const end = axis === "horizontal" ? rect.bottom : rect.right;
  const length = end - start;
  // A thinner vertical child is usually a dialogue column inside the same
  // panel rather than a readable manga panel in its own right.
  const minimumChild = axis === "horizontal"
    ? Math.max(20, Math.round(pageHeight * 0.095))
    : Math.max(24, Math.round(pageWidth * 0.22));
  // A perfectly valid printed gutter can collapse to a single sample column
  // when a tall phone page is reduced to the bounded analysis canvas.
  const minimumGutter = Math.max(1, Math.round(length * 0.004));
  // Real manga gutters stay narrow. Broad low-ink bands are usually the white
  // interior of a speech balloon and must not become their own Guided panel.
  const maximumGutter = Math.max(minimumGutter, Math.round(length * 0.12));
  const maximumInkRatio = 0.035;
  const bands: Array<{ start: number; end: number; averageInk: number }> = [];
  let bandStart: number | null = null;
  let bandInk = 0;

  for (let coordinate = start; coordinate < end; coordinate += 1) {
    const ratio = inkRatio(ink, imageWidth, rect, axis, coordinate);
    if (ratio <= maximumInkRatio) {
      if (bandStart === null) bandStart = coordinate;
      bandInk += ratio;
    } else if (bandStart !== null) {
      bands.push({ start: bandStart, end: coordinate, averageInk: bandInk / (coordinate - bandStart) });
      bandStart = null;
      bandInk = 0;
    }
  }
  if (bandStart !== null) bands.push({ start: bandStart, end, averageInk: bandInk / (end - bandStart) });

  const candidates = bands
    .filter((band) => band.end - band.start >= minimumGutter)
    .filter((band) => band.end - band.start <= maximumGutter)
    .filter((band) => band.start - start >= minimumChild && end - band.end >= minimumChild)
    .map((band) => {
      const before = band.start - start;
      const after = end - band.end;
      const balance = Math.min(before, after) / Math.max(before, after);
      const relativeWidth = (band.end - band.start) / length;
      const clean = 1 - band.averageInk / maximumInkRatio;
      const horizontalBias = axis === "horizontal" ? 1.12 : 1;
      return {
        axis,
        start: band.start,
        end: band.end,
        score: horizontalBias * (relativeWidth * 5 + clean * 0.45 + balance * 0.28)
      } satisfies Separator;
    });

  return candidates.sort((left, right) => right.score - left.score)[0] ?? null;
}

function trimWhitespace(ink: Uint8Array, width: number, height: number, rect: PixelRect): PixelRect {
  const rowHasContent = (y: number) => inkRatio(ink, width, rect, "horizontal", y) > 0.012;
  const columnHasContent = (x: number) => inkRatio(ink, width, rect, "vertical", x) > 0.012;
  let top = rect.top;
  let bottom = rect.bottom;
  let left = rect.left;
  let right = rect.right;
  while (top < rect.bottom - 1 && !rowHasContent(top)) top += 1;
  while (bottom > top + 1 && !rowHasContent(bottom - 1)) bottom -= 1;
  while (left < rect.right - 1 && !columnHasContent(left)) left += 1;
  while (right > left + 1 && !columnHasContent(right - 1)) right -= 1;
  if (top >= bottom || left >= right) return rect;
  const horizontalPadding = Math.max(1, Math.round(width * 0.008));
  const verticalPadding = Math.max(1, Math.round(height * 0.006));
  return {
    left: Math.max(0, left - horizontalPadding),
    top: Math.max(0, top - verticalPadding),
    right: Math.min(width, right + horizontalPadding),
    bottom: Math.min(height, bottom + verticalPadding)
  };
}

function splitPanels(
  ink: Uint8Array,
  imageWidth: number,
  imageHeight: number,
  rect: PixelRect,
  readingDirection: "rtl" | "ltr",
  depth = 0
): PixelRect[] {
  if (depth >= 6) return [rect];
  const horizontal = separatorFor(ink, imageWidth, rect, "horizontal", imageWidth, imageHeight);
  const vertical = separatorFor(ink, imageWidth, rect, "vertical", imageWidth, imageHeight);
  const separator = !horizontal
    ? vertical
    : !vertical
      ? horizontal
      : horizontal.score >= vertical.score ? horizontal : vertical;
  if (!separator) return [rect];

  const midpoint = Math.round((separator.start + separator.end) / 2);
  if (separator.axis === "horizontal") {
    return [
      ...splitPanels(ink, imageWidth, imageHeight, { ...rect, bottom: midpoint }, readingDirection, depth + 1),
      ...splitPanels(ink, imageWidth, imageHeight, { ...rect, top: midpoint }, readingDirection, depth + 1)
    ].slice(0, maxDetectedPanels);
  }

  const left = { ...rect, right: midpoint };
  const right = { ...rect, left: midpoint };
  const ordered = readingDirection === "rtl" ? [right, left] : [left, right];
  return ordered
    .flatMap((child) => splitPanels(ink, imageWidth, imageHeight, child, readingDirection, depth + 1))
    .slice(0, maxDetectedPanels);
}

export function detectMangaPanelsFromPixels(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  readingDirection: "rtl" | "ltr" = "rtl"
): MangaPanel[] {
  if (width <= 0 || height <= 0 || pixels.length < width * height * 4) return [];
  const ink = new Uint8Array(width * height);
  for (let index = 0; index < width * height; index += 1) {
    const red = pixels[index * 4];
    const green = pixels[index * 4 + 1];
    const blue = pixels[index * 4 + 2];
    const alpha = pixels[index * 4 + 3] / 255;
    const luminance = (red * 0.2126 + green * 0.7152 + blue * 0.0722) * alpha + 255 * (1 - alpha);
    const colourSpread = Math.max(red, green, blue) - Math.min(red, green, blue);
    ink[index] = luminance < 242 || colourSpread > 22 ? 1 : 0;
  }

  const page = trimWhitespace(ink, width, height, { left: 0, top: 0, right: width, bottom: height });
  const rectangles = splitPanels(ink, width, height, page, readingDirection)
    .map((rect) => trimWhitespace(ink, width, height, rect));
  return rectangles.map((rect) => ({
    x: clamp(rect.left / width, 0, 1),
    y: clamp(rect.top / height, 0, 1),
    width: clamp((rect.right - rect.left) / width, 0, 1),
    height: clamp((rect.bottom - rect.top) / height, 0, 1)
  }));
}

export function loadMangaPanels(
  imageUrl: string,
  readingDirection: "rtl" | "ltr" = "rtl"
): Promise<MangaPanel[]> {
  const cacheKey = `${readingDirection}:${imageUrl}`;
  const cached = panelCache.get(cacheKey);
  if (cached) return cached;

  const request = new Promise<MangaPanel[]>((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => {
      try {
        const scale = Math.min(1, 360 / Math.max(image.naturalWidth, image.naturalHeight));
        const width = Math.max(1, Math.round(image.naturalWidth * scale));
        const height = Math.max(1, Math.round(image.naturalHeight * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) throw new Error("Panel detection is unavailable in this browser");
        context.drawImage(image, 0, 0, width, height);
        const pixels = context.getImageData(0, 0, width, height).data;
        const panels = detectMangaPanelsFromPixels(pixels, width, height, readingDirection);
        resolve(panels.length > 0 ? panels : [{ x: 0, y: 0, width: 1, height: 1 }]);
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = () => reject(new Error("Could not prepare this page for Guided View"));
    image.src = imageUrl;
  });
  panelCache.set(cacheKey, request);
  request.catch(() => panelCache.delete(cacheKey));
  return request;
}
